import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Button from './Button';
import Input from './Input';
import Loader from './Loader';
import ResultFlowGraph from './ResultFlowGraph';

const GET_RESULT_FOR_VISUALISATION = gql`
  query getResultForVisualisation($resultId: String!) {
    getResultForVisualisation(resultId: $resultId) {
      result {
        id
        surveyId
        surveyName
        userId
        language
        complete
        campaignFailure
        notCounted
        deleted
        fromWebSurvey
        percentageCompleted
        createdAt
        totalQuestions
        answeredQuestions
      }
      path {
        order
        questionId
        type
        questionText
      }
      graphEdges {
        from
        to
        kind
        choiceLabel
        taken
      }
      groups {
        id
        label
        parentId
      }
      items {
        questionId
        type
        questionText
        mediaUrl
        groupId
        groupLabel
        inStructure
        visited
        order
        finishedAt
        options {
          index
          text
          imageUrl
          selected
          rank
          kind
        }
        openAnswer
        otherAnswer
        sliderValue
        sliderMin
        sliderMax
        mediaAnswerUrl
        matrix {
          xQuestionText
          yQuestionText
          dimensions {
            axis
            questionText
            scaleLabels
            rows {
              label
              selectedColumns
              selectedLabels
            }
          }
        }
      }
    }
  }
`;

const TYPE_LABELS = {
  mc_question: 'Multiple choice',
  open_question: 'Open question',
  slider: 'Slider',
  matrix: 'Matrix',
  message: 'Message',
  video: 'Video',
  iframe: 'Iframe',
  camera: 'Photo',
  video_camera: 'Video recording',
  voice: 'Voice',
  distributor: 'Distributor',
  unknown: 'Question'
};

const typeLabel = type => TYPE_LABELS[type] || type;

const formatDate = value => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

function Badge({ text, color, background }) {
  return <span style={{ ...styles.badge, color, backgroundColor: background }}>
    {text}
  </span>
}

function StatusBadges({ result }) {
  const badges = [];
  if (result.campaignFailure) {
    badges.push({
      key: 'failed',
      text: 'Screened out',
      color: '#8a4b00',
      background: '#ffe7c2'
    });
  } else if (result.complete) {
    badges.push({
      key: 'complete',
      text: 'Completed',
      color: '#1e7e34',
      background: '#d8f3df'
    });
  } else {
    badges.push({
      key: 'incomplete',
      text: 'In progress / abandoned',
      color: '#555',
      background: '#eee'
    });
  }
  if (result.notCounted) {
    badges.push({
      key: 'notcounted',
      text: 'Not counted (quota)',
      color: '#8a4b00',
      background: '#fff0d6'
    });
  }
  if (result.deleted) {
    badges.push({
      key: 'test',
      text: 'Test / deleted',
      color: '#6a1b9a',
      background: '#f1e0fb'
    });
  }
  if (result.fromWebSurvey) {
    badges.push({
      key: 'web',
      text: 'Web survey',
      color: '#1f6fb2',
      background: '#e3f1fb'
    });
  }
  return (
    <div style={styles.badgeRow}>
      {badges.map(b => (
        <Badge
          key={b.key}
          text={b.text}
          color={b.color}
          background={b.background}
        />
      ))}
    </div>
  );
}

function McOptions({ item }) {
  return <div>
    {item.options.map(option => (
      <div
        key={option.index}
        style={{
          ...styles.option,
          ...(option.selected ? styles.optionSelected : {})
        }}
      >
        <span style={styles.optionMarker}>{option.selected ? '✓' : ''}</span>
        {option.rank ? (
          <span style={styles.rankBadge}>{option.rank}</span>
        ) : null}
        {option.imageUrl ? (
          <img src={option.imageUrl} alt="" style={styles.optionImage} />
        ) : null}
        <span style={option.selected ? styles.optionTextSelected : undefined}>
          {option.text || <em style={styles.muted}>(empty option)</em>}
          {option.kind === 'other' ? ' (other)' : ''}
          {option.kind === 'none_of_the_above' ? ' (none of the above)' : ''}
        </span>
      </div>
    ))}
    {item.otherAnswer ? (
      <div style={styles.otherAnswer}>
        Other answer: <strong>{item.otherAnswer}</strong>
      </div>
    ) : null}
  </div>
}

function SliderAnswer({ item }) {
  const hasRange =
    typeof item.sliderMin === 'number' && typeof item.sliderMax === 'number';
  let position = 0;
  if (hasRange && item.sliderMax > item.sliderMin && item.sliderValue != null) {
    position =
      ((item.sliderValue - item.sliderMin) /
        (item.sliderMax - item.sliderMin)) *
      100;
  }
  return (
    <div>
      <div style={styles.sliderValue}>
        {item.sliderValue != null ? (
          item.sliderValue
        ) : (
          <em style={styles.muted}>no value</em>
        )}
        {hasRange ? (
          <span style={styles.muted}>
            {' '}
            (range {item.sliderMin}–{item.sliderMax})
          </span>
        ) : null}
      </div>
      {hasRange && item.sliderValue != null ? (
        <div style={styles.sliderTrack}>
          <div style={{ ...styles.sliderMarker, left: `${position}%` }} />
        </div>
      ) : null}
    </div>
  );
}

function MatrixAnswer({ matrix }) {
  return <div>
    {matrix.dimensions && matrix.dimensions.length ? (
      matrix.dimensions.map(dimension => (
        <div key={dimension.axis} style={styles.matrixDimension}>
          {dimension.questionText ? (
            <div style={styles.matrixDimensionTitle}>
              {dimension.axis === 'y' ? 'Y · ' : 'X · '}
              {dimension.questionText}
            </div>
          ) : null}
          <table style={styles.matrixTable}>
            <tbody>
              {dimension.rows.map(row => (
                <tr key={row.label}>
                  <td style={styles.matrixRowLabel}>{row.label}</td>
                  <td style={styles.matrixRowValue}>
                    {row.selectedLabels && row.selectedLabels.length
                      ? row.selectedLabels.join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))
    ) : (
      <span style={styles.muted}>No matrix selections recorded.</span>
    )}
  </div>
}

function AnswerBody({ item }) {
  if (!item.visited) {
    return <div style={styles.notReached}>Not reached in this result.</div>;
  }
  switch (item.type) {
    case 'mc_question':
      return <McOptions item={item} />;
    case 'open_question':
      return (
        <div style={styles.openAnswer}>
          {item.openAnswer ? (
            item.openAnswer
          ) : (
            <em style={styles.muted}>(empty answer)</em>
          )}
        </div>
      );
    case 'slider':
      return <SliderAnswer item={item} />;
    case 'matrix':
      return item.matrix ? (
        <MatrixAnswer matrix={item.matrix} />
      ) : (
        <span style={styles.muted}>No matrix answer.</span>
      );
    case 'camera':
    case 'video_camera':
    case 'voice':
      return item.mediaAnswerUrl ? (
        <a href={item.mediaAnswerUrl} target="_blank" rel="noopener noreferrer">
          View uploaded media
        </a>
      ) : (
        <span style={styles.muted}>No media uploaded.</span>
      );
    default:
      return <span style={styles.muted}>Informational block (no answer).</span>;
  }
}

function ResultItem({ item }) {
  return <div
    style={{
      ...styles.item,
      ...(item.visited ? {} : styles.itemSkipped)
    }}
  >
    <div style={styles.itemRail}>
      <div
        style={{
          ...styles.stepBadge,
          ...(item.visited ? styles.stepBadgeVisited : styles.stepBadgeSkipped)
        }}
      >
        {item.visited && item.order != null ? item.order + 1 : '–'}
      </div>
    </div>
    <div style={styles.itemBody}>
      <div style={styles.itemHeader}>
        <Badge
          text={typeLabel(item.type)}
          color="#34495e"
          background="#eef1f4"
        />
        {item.groupLabel ? (
          <Badge text={item.groupLabel} color="#1f6fb2" background="#e3f1fb" />
        ) : null}
        {!item.inStructure ? (
          <Badge
            text="not in the survey flow"
            color="#8a4b00"
            background="#fff0d6"
          />
        ) : null}
      </div>
      <div style={styles.questionText}>
        {item.questionText || <em style={styles.muted}>(no question text)</em>}
      </div>
      {item.mediaUrl ? (
        <img src={item.mediaUrl} alt="" style={styles.questionMedia} />
      ) : null}
      <div style={styles.answerBody}>
        <AnswerBody item={item} />
      </div>
    </div>
  </div>
}

function ResultAnswersVisualiser() {
  const [resultId, setResultId] = useState('');

  const [fetchResult, { loading, error, data }] = useLazyQuery(
    GET_RESULT_FOR_VISUALISATION,
    { fetchPolicy: 'network-only' }
  );

  const handleLoad = () => {
    if (resultId.trim()) {
      fetchResult({ variables: { resultId: resultId.trim() } });
    }
  };

  const payload = data && data.getResultForVisualisation;
  const meta = payload && payload.result;

  return (
    <div style={styles.container}>
      <Link to="/development-dashboard" style={styles.backLink}>
        &larr; Back to dashboard
      </Link>
      <h1>Result answers visualiser</h1>
      <p>
        Enter a completed result id to see the survey structure together with
        the answers the user gave and the path they took.
      </p>

      <div style={styles.form}>
        <Input
          type="text"
          value={resultId}
          onChange={e => setResultId(e.target.value)}
          placeholder="Enter result id"
          style={styles.input}
        />
        <Button onClick={handleLoad} primary style={styles.loadButton}>
          Load result
        </Button>
      </div>

      {loading && <Loader isProcessing />}
      {error && <p style={styles.errorText}>Error: {error.message}</p>}

      {meta && (
        <div style={styles.meta}>
          <div style={styles.metaHeader}>
            <h2 style={styles.surveyName}>
              {meta.surveyName || 'Unknown survey'}
            </h2>
            <StatusBadges result={meta} />
          </div>
          <div style={styles.metaGrid}>
            <span>
              <strong>Result:</strong> {meta.id}
            </span>
            <span>
              <strong>Survey:</strong> {meta.surveyId || '-'}
            </span>
            <span>
              <strong>User:</strong> {meta.userId || '-'}
            </span>
            <span>
              <strong>Language:</strong> {meta.language || '-'}
            </span>
            <span>
              <strong>Answered:</strong> {meta.answeredQuestions}/
              {meta.totalQuestions}
            </span>
            <span>
              <strong>Completed:</strong>{' '}
              {meta.percentageCompleted != null
                ? `${meta.percentageCompleted}%`
                : '-'}
            </span>
            <span>
              <strong>Date:</strong> {formatDate(meta.createdAt) || '-'}
            </span>
          </div>
        </div>
      )}

      {payload && payload.items && payload.items.length > 0 && (
        <div style={styles.itemsContainer}>
          <h3 style={styles.sectionTitle}>Flow graph</h3>
          <ResultFlowGraph
            items={payload.items}
            graphEdges={payload.graphEdges}
            groups={payload.groups}
          />
        </div>
      )}

      {payload && payload.path && payload.path.length > 0 && (
        <div style={styles.pathContainer}>
          <h3 style={styles.sectionTitle}>Flow path</h3>
          <div style={styles.pathRow}>
            {payload.path.map((step, index) => (
              <React.Fragment key={`${step.questionId}-${step.order}`}>
                <span style={styles.pathChip} title={step.questionText}>
                  <span style={styles.pathChipIndex}>{index + 1}</span>
                  {typeLabel(step.type)}
                  <span style={styles.pathChipId}>{step.questionId}</span>
                </span>
                {index < payload.path.length - 1 ? (
                  <span style={styles.pathArrow}>→</span>
                ) : null}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {payload && payload.items && payload.items.length > 0 && (
        <div style={styles.itemsContainer}>
          <h3 style={styles.sectionTitle}>Survey structure &amp; answers</h3>
          {payload.items.map(item => (
            <ResultItem key={item.questionId} item={item} />
          ))}
        </div>
      )}

      {payload && (!payload.items || payload.items.length === 0) && (
        <p style={styles.muted}>This result has no questions to display.</p>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px',
    maxWidth: '900px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600
  },
  form: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
    maxWidth: '600px'
  },
  input: {
    flexGrow: 1
  },
  loadButton: {
    minWidth: '130px'
  },
  errorText: {
    color: '#c0392b'
  },
  meta: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
    backgroundColor: '#fafafa'
  },
  metaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '8px'
  },
  surveyName: {
    margin: 0,
    fontSize: '18px'
  },
  badgeRow: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },
  badge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600
  },
  metaGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px 20px',
    marginTop: '12px',
    fontSize: '13px',
    color: '#444'
  },
  sectionTitle: {
    fontSize: '15px',
    margin: '0 0 10px 0'
  },
  pathContainer: {
    marginBottom: '24px'
  },
  pathRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px'
  },
  pathChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '14px',
    backgroundColor: '#eef1f4',
    fontSize: '12px',
    color: '#34495e'
  },
  pathChipIndex: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#34495e',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700
  },
  pathChipId: {
    fontFamily: 'monospace',
    fontSize: '11px',
    color: '#8a93a0'
  },
  pathArrow: {
    color: '#aaa'
  },
  itemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  item: {
    display: 'flex',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#fff'
  },
  itemSkipped: {
    opacity: 0.55
  },
  itemRail: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    width: '52px',
    backgroundColor: '#f6f8fa',
    flexShrink: 0
  },
  stepBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontWeight: 700,
    fontSize: '13px'
  },
  stepBadgeVisited: {
    backgroundColor: '#1f6fb2',
    color: '#fff'
  },
  stepBadgeSkipped: {
    backgroundColor: '#ddd',
    color: '#888'
  },
  itemBody: {
    padding: '14px 16px',
    flexGrow: 1
  },
  itemHeader: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '8px'
  },
  questionText: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#222',
    whiteSpace: 'pre-wrap',
    marginBottom: '8px'
  },
  questionMedia: {
    maxWidth: '160px',
    maxHeight: '120px',
    borderRadius: '6px',
    display: 'block',
    marginBottom: '8px'
  },
  answerBody: {
    marginTop: '4px'
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid #eee',
    marginBottom: '4px',
    fontSize: '14px'
  },
  optionSelected: {
    border: '1px solid #1e7e34',
    backgroundColor: '#e7f6ec'
  },
  optionTextSelected: {
    fontWeight: 600,
    color: '#1e7e34'
  },
  optionMarker: {
    display: 'inline-flex',
    justifyContent: 'center',
    width: '16px',
    color: '#1e7e34',
    fontWeight: 700
  },
  rankBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#1e7e34',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700
  },
  optionImage: {
    width: '32px',
    height: '32px',
    objectFit: 'cover',
    borderRadius: '4px'
  },
  otherAnswer: {
    marginTop: '4px',
    fontSize: '13px',
    color: '#444'
  },
  openAnswer: {
    padding: '10px 12px',
    borderLeft: '3px solid #1f6fb2',
    backgroundColor: '#f5f9fc',
    borderRadius: '4px',
    fontSize: '14px',
    whiteSpace: 'pre-wrap'
  },
  sliderValue: {
    fontSize: '15px',
    fontWeight: 600
  },
  sliderTrack: {
    position: 'relative',
    height: '6px',
    backgroundColor: '#e0e0e0',
    borderRadius: '3px',
    marginTop: '8px',
    maxWidth: '320px'
  },
  sliderMarker: {
    position: 'absolute',
    top: '-4px',
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    backgroundColor: '#1f6fb2',
    transform: 'translateX(-50%)'
  },
  matrixDimension: {
    marginBottom: '10px'
  },
  matrixDimensionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    marginBottom: '4px',
    color: '#444'
  },
  matrixTable: {
    borderCollapse: 'collapse',
    width: '100%',
    maxWidth: '480px'
  },
  matrixRowLabel: {
    border: '1px solid #eee',
    padding: '4px 8px',
    fontSize: '13px',
    color: '#333'
  },
  matrixRowValue: {
    border: '1px solid #eee',
    padding: '4px 8px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#1e7e34'
  },
  notReached: {
    fontSize: '13px',
    color: '#999',
    fontStyle: 'italic'
  },
  muted: {
    color: '#999'
  }
};

export default ResultAnswersVisualiser;
